"use client";

import { useSession } from "next-auth/react";
import { useEffect } from "react";
import {
  registerTimerServiceWorker,
  syncWorkTimerNotifications,
  type WorkTimerNotification,
} from "@/lib/timerNotificationClient";

function normalizeTimer(value: any): WorkTimerNotification | null {
  if (!value || typeof value !== "object") return null;
  const jobId = typeof value.jobId === "string" ? value.jobId : "";
  const status = value.status === "running" || value.status === "paused" ? value.status : null;
  if (!jobId || !status) return null;
  return {
    jobId,
    status,
    startedAt: typeof value.startedAt === "string" ? value.startedAt : null,
    lastStartedAt: typeof value.lastStartedAt === "string" ? value.lastStartedAt : null,
    accumulatedMs: Number.isFinite(Number(value.accumulatedMs)) ? Number(value.accumulatedMs) : 0,
    jobTitle: typeof value.jobTitle === "string" ? value.jobTitle : null,
  };
}

async function readTimers() {
  try {
    const res = await fetch("/api/work-timers", { cache: "no-store" });
    if (!res.ok) return [];
    const data = await res.json().catch(() => []);
    return (Array.isArray(data) ? data : [])
      .map((entry) => normalizeTimer(entry))
      .filter((entry): entry is WorkTimerNotification => Boolean(entry));
  } catch {
    return [];
  }
}

export default function TimerNotificationBridge() {
  const { status } = useSession();

  useEffect(() => {
    if (status !== "authenticated") return;
    void registerTimerServiceWorker();

    let active = true;
    const refresh = async () => {
      const timers = await readTimers();
      if (!active) return;
      await syncWorkTimerNotifications(timers);
    };

    void refresh();
    window.addEventListener("work-timer-change", refresh);
    return () => {
      active = false;
      window.removeEventListener("work-timer-change", refresh);
    };
  }, [status]);

  return null;
}
